/**
 * よく一緒に購入される商品 (Frequently bought together)
 *
 * 勾选多个关联商品后，用 Shopify 的批量加购接口（/cart/add.js 的 items 数组）
 * 一次性加入购物车，并复用主题的 cart-drawer / cart-icon 刷新机制。
 *
 * Dependencies:
 * - theme.routes.cartAdd / theme.routes.cart / theme.settings.afterAtc
 * - <cart-drawer> 的 renderContents()（若启用抽屉）
 */
if (!customElements.get('frequently-bought')) {
  class FrequentlyBought extends HTMLElement {
    connectedCallback() {
      this.errorEl = this.querySelector('.fbt__error');
      this.checks = Array.from(this.querySelectorAll('.fbt__check'));
      this.cartDrawer = document.querySelector('cart-drawer');
      this.addBtn = this.querySelector('[data-fbt-add]');

      // Toggle the item's checkbox when clicking anywhere in the item, except on
      // interactive controls (checkbox, links, quantity buttons/input).
      this.boundHandleItemClick = this.handleItemClick.bind(this);
      this.querySelectorAll('.fbt__item').forEach((item) => {
        item.addEventListener('click', this.boundHandleItemClick);
      });

      if (this.addBtn) {
        this.boundHandleAdd = this.handleAdd.bind(this);
        this.boundUpdateState = this.updateButtonState.bind(this);
        this.addBtn.addEventListener('click', this.boundHandleAdd);
        this.addEventListener('change', this.boundUpdateState);
        this.updateButtonState();
      }

      this.initListCollapse();
    }

    disconnectedCallback() {
      this.querySelectorAll('.fbt__item').forEach((item) => {
        item.removeEventListener('click', this.boundHandleItemClick);
      });
      if (this.addBtn) {
        this.addBtn.removeEventListener('click', this.boundHandleAdd);
        this.removeEventListener('change', this.boundUpdateState);
      }
      if (this.boundHandleListToggle && this.listToggle) {
        this.listToggle.removeEventListener('click', this.boundHandleListToggle);
      }
    }

    /**
     * Collapses long lists to ~1.5 items with a show-more control.
     */
    initListCollapse() {
      const wrap = this.querySelector('.fbt__list-wrap.is-collapsed');
      if (!wrap) return;

      const list = wrap.querySelector('.fbt__list');
      this.listToggle = wrap.querySelector('.fbt__list-toggle');
      if (!list) return;

      const items = list.querySelectorAll('.fbt__item');
      if (items.length <= 1) {
        wrap.classList.remove('is-collapsed');
        if (this.listToggle) this.listToggle.hidden = true;
        return;
      }

      const setCollapsedHeight = () => {
        if (!wrap.classList.contains('is-collapsed') || !items[1]) return;
        const listTop = list.getBoundingClientRect().top;
        const secondRect = items[1].getBoundingClientRect();
        const height = Math.round(secondRect.top + (secondRect.height * 0.5) - listTop);
        if (height > 0) {
          list.style.setProperty('--fbt-list-collapsed-height', `${height}px`);
          wrap.dataset.measured = '';
        }
      };

      setCollapsedHeight();
      requestAnimationFrame(setCollapsedHeight);
      list.querySelectorAll('img').forEach((img) => {
        if (!img.complete) {
          img.addEventListener('load', setCollapsedHeight, { once: true });
        }
      });

      if (this.listToggle) {
        this.boundHandleListToggle = (evt) => {
          evt.preventDefault();
          const willCollapse = !wrap.classList.contains('is-collapsed');

          if (willCollapse) {
            wrap.classList.add('is-collapsed');
            setCollapsedHeight();
            this.listToggle.setAttribute('aria-expanded', 'false');
            if (this.listToggle.dataset.showMoreLabel) {
              this.listToggle.setAttribute('aria-label', this.listToggle.dataset.showMoreLabel);
            }
          } else {
            const fullHeight = list.scrollHeight;
            list.style.setProperty('--fbt-list-collapsed-height', `${fullHeight}px`);
            wrap.classList.remove('is-collapsed');
            requestAnimationFrame(() => {
              list.style.removeProperty('--fbt-list-collapsed-height');
            });
            this.listToggle.setAttribute('aria-expanded', 'true');
            if (this.listToggle.dataset.showLessLabel) {
              this.listToggle.setAttribute('aria-label', this.listToggle.dataset.showLessLabel);
            }
          }
        };
        this.listToggle.addEventListener('click', this.boundHandleListToggle);
      }
    }

    /**
     * Toggles the checkbox when clicking the item area.
     * @param {object} evt - Event object.
     */
    handleItemClick(evt) {
      // Let interactive controls and the native label keep their own behaviour.
      if (evt.target.closest('.fbt__qty, a, button, input, select, label')) return;

      const check = evt.currentTarget.querySelector('.fbt__check');
      if (!check || check.disabled) return;

      check.checked = !check.checked;
      check.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * Collects the selected variant ids and their quantities.
     * @returns {Array<{id: string, quantity: number}>}
     */
    getSelectedItems() {
      return this.checks
        .filter((check) => check.checked && !check.disabled && check.dataset.variantId)
        .map((check) => {
          const qtyEl = this.querySelector(
            `.fbt__qty-input[data-qty-for="${check.dataset.variantId}"]`
          );
          const quantity = qtyEl ? Math.max(1, parseInt(qtyEl.value, 10) || 1) : 1;
          return { id: check.dataset.variantId, quantity };
        });
    }

    /**
     * Enables/disables the add button based on the current selection.
     */
    updateButtonState() {
      const hasSelection = this.checks.some((check) => check.checked && !check.disabled);
      this.addBtn.disabled = !hasSelection;
    }

    /**
     * Shows/hides the error message.
     * @param {string} [message] - Message to show. Falsy hides the alert.
     */
    setError(message) {
      if (!this.errorEl) return;
      this.errorEl.hidden = !message;
      this.errorEl.textContent = message || '';
    }

    /**
     * Handles the "add selected to cart" action.
     */
    async handleAdd() {
      const items = this.getSelectedItems();
      if (!items.length) return;

      this.setError('');
      this.addBtn.setAttribute('aria-disabled', 'true');
      this.addBtn.classList.add('is-loading');

      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items,
            sections,
            sections_url: window.location.pathname
          })
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          this.setError(data.description || data.message || '');
          throw new Error(data.status || response.status);
        }

        // Update the cart icon count.
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble && data.sections && data.sections['cart-icon-bubble']) {
          bubble.innerHTML = data.sections['cart-icon-bubble'];
        }

        if (theme.settings.afterAtc === 'page') {
          window.location.href = theme.routes.cart;
          return;
        }

        if (this.cartDrawer) {
          this.cartDrawer.renderContents(data, theme.settings.afterAtc === 'drawer');
        } else if (window.location.pathname === theme.routes.cart) {
          window.location.reload();
        }

        this.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: { items }
        }));
      } catch (error) {
        console.log(error); // eslint-disable-line
        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        this.addBtn.removeAttribute('aria-disabled');
        this.addBtn.classList.remove('is-loading');
      }
    }
  }

  customElements.define('frequently-bought', FrequentlyBought);
}
